import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CardAvatar } from '@sorare/core/src/components/card/Avatar';
import { LiveDot } from '@sorare/core/src/components/live/LiveDot';
import { Captain } from '@sorare/core/src/components/so5/Captain';

import { isGameLive } from '@sorare/marketplace/src/shared/game/lib';

import { So5AppearanceScore } from '#src/components/appearance/So5AppearanceScore/index.tsx';

import type { CompactSo5Lineup_so5Lineup } from './__generated__/index.graphql.ts';

const AvatarWrapper = styled.div`
  position: relative;
`;
const CaptainWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--half-unit));
  left: calc(-1 * var(--half-unit));
`;
const Appearances = styled(Horizontal)`
  --border-radius: var(--unit);
  padding-top: var(--half-unit);
  align-items: flex-start;
  display: grid;

  & > * {
    flex-grow: 1;
  }
`;
const StyledCardAvatar = styled(CardAvatar)`
  &.cancelled {
    filter: grayscale(1);
  }
  & > img {
    border-radius: calc(var(--unit) - 1px);
    max-width: 100%;
    width: 100px;
    aspect-ratio: 1;
  }
`;

type Props = {
  so5Lineup: CompactSo5Lineup_so5Lineup;
};
export const CompactSo5Lineup = ({ so5Lineup }: Props) => {
  const { so5Appearances } = so5Lineup;
  return (
    <Appearances
      style={{ gridTemplateColumns: `repeat(${so5Appearances.length}, 1fr)` }}
    >
      {so5Appearances.map(appearance => (
        <Vertical key={appearance.id} center>
          {!!appearance.anyCard && (
            <AvatarWrapper>
              <StyledCardAvatar
                className={classNames({
                  cancelled: !!so5Lineup?.cancelledAt,
                })}
                key={appearance.id}
                card={appearance.anyCard}
              />
              {!!appearance.captain && (
                <CaptainWrapper>
                  <Captain active size={2} />
                </CaptainWrapper>
              )}
            </AvatarWrapper>
          )}
          <Horizontal gap={0.5}>
            {appearance.eligiblePlayerGameScores.some(pgs =>
              isGameLive(pgs.anyGame.statusTyped)
            ) && <LiveDot size="sm" animate />}
            <So5AppearanceScore
              so5Appearance={appearance}
              hideReviewing
              withDate
            />
          </Horizontal>
        </Vertical>
      ))}
    </Appearances>
  );
};

CompactSo5Lineup.fragments = {
  so5Lineup: gql`
    fragment CompactSo5Lineup_so5Lineup on So5Lineup {
      id
      cancelledAt
      so5Appearances {
        id
        captain
        anyCard {
          slug
          ...CardAvatar_anyCard
        }
        eligiblePlayerGameScores {
          id
          anyGame {
            id
            statusTyped
          }
        }
        ...So5AppearanceScore_so5Appearance
      }
    }
    ${CardAvatar.fragments.anyCard}
    ${So5AppearanceScore.fragments.so5Appearance}
  ` as TypedDocumentNode<CompactSo5Lineup_so5Lineup>,
};
